import { createRoot } from 'react-dom/client'
import * as Sentry from "@sentry/react";
import App from './App.tsx'
// Inter ships with the app (was Google Fonts): no network font load, works offline.
// Latin subset only (covers EN/FR/NL; any rarer glyph falls back to the system
// font). The full packages also ship Cyrillic, Greek, Vietnamese and Latin
// Extended files. Subset files carry no unicode-range, so import just one per weight.
import '@fontsource/inter/latin-300.css'
import '@fontsource/inter/latin-400.css'
import '@fontsource/inter/latin-500.css'
import '@fontsource/inter/latin-600.css'
import '@fontsource/inter/latin-700.css'
import '@fontsource/inter/latin-800.css'
import '@fontsource/inter/latin-900.css'
import './index.css'
import './styles/brand.css'

// CRITICAL: Polyfill for crypto.randomUUID (must load before any other code)
import './utils/uuid-polyfill'

// Mobile debug helper
const addDebug = (window as any).addDebug || ((msg: string) => console.log('[DEBUG]', msg));

// DEFENSE LAYER 4: Debug Instrumentation - Catch circular dependency errors at runtime
window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('before initialization')) {
    console.error('🚨 CIRCULAR DEPENDENCY ERROR DETECTED AT RUNTIME');
    console.error('Message:', event.message);
    console.error('File:', event.filename);
    console.error('Line:', event.lineno);
    console.error('Stack:', event.error?.stack);
    console.error('This indicates manual chunking or import ordering issues');
    // Don't preventDefault - let the error propagate to Sentry
  }

  // CRITICAL: Chunk loading error recovery
  // Detects when lazy-loaded chunks fail due to stale cache and forces a reload
  const isChunkError = event.message?.includes('Failed to fetch dynamically imported module') ||
                       event.message?.includes('text/html') ||
                       event.message?.includes('not a valid JavaScript MIME type') ||
                       event.message?.includes('Importing a module script failed');

  if (isChunkError && import.meta.env.PROD) {
    console.error('🚨 CHUNK LOAD FAILED - Stale cache detected');
    addDebug('Chunk error detected, attempting recovery...');

    // Mark that we're reloading to prevent infinite loops
    const reloadKey = 'chunk_reload_attempted';
    const lastReload = sessionStorage.getItem(reloadKey);
    const now = Date.now();

    // Only reload once per session, and not more than once per minute
    if (!lastReload || (now - parseInt(lastReload)) > 60000) {
      sessionStorage.setItem(reloadKey, now.toString());
      addDebug('Clearing cache and reloading...');

      // Clear all caches before reload
      if ('caches' in window) {
        caches.keys().then(names => {
          Promise.all(names.map(name => caches.delete(name)))
            .then(() => {
              addDebug('Cache cleared, reloading now');
              window.location.reload();
            });
        });
      } else {
        window.location.reload();
      }

      event.preventDefault(); // Prevent error from showing to user
    } else {
      console.error('❌ Reload already attempted. Manual cache clear needed.');
      addDebug('ERROR: Reload failed, manual intervention needed');
    }
  }
});

addDebug('main.tsx starting...');

try {
  Sentry.init({
    dsn: "https://7332c6da8b0a76cde921e0c8bc80674f@o4509575277576192.ingest.de.sentry.io/4509575278755920",

    // Environment detection
    environment: import.meta.env.PROD ? 'production' : 'development',

    // Release tracking - helps identify which version has issues
    release: import.meta.env.VITE_APP_VERSION || 'unknown',

    // Setting this option to true will send default PII data to Sentry.
    sendDefaultPii: true,

    // Performance monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev

    // Session replay for debugging (only in production, 10% sample)
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0, // Always capture replay on error

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network errors that are expected
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      // User-initiated navigation
      'AbortError',
    ],

    // Security: Tag auth-related errors for alerting
    beforeSend(event, hint) {
      // Tag security-relevant errors
      const errorMessage = event.message || hint.originalException?.toString() || '';

      if (
        errorMessage.includes('auth') ||
        errorMessage.includes('401') ||
        errorMessage.includes('403') ||
        errorMessage.includes('unauthorized') ||
        errorMessage.includes('permission')
      ) {
        event.tags = {
          ...event.tags,
          security_relevant: 'true',
          error_category: 'auth',
        };
      }

      // Tag payment-related errors
      if (
        errorMessage.includes('stripe') ||
        errorMessage.includes('payment') ||
        errorMessage.includes('charge')
      ) {
        event.tags = {
          ...event.tags,
          security_relevant: 'true',
          error_category: 'payment',
        };
      }

      return event;
    },
  });
  addDebug('Sentry initialized');
} catch (e) {
  addDebug('Sentry failed: ' + (e as Error).message);
}

// CRITICAL: Listen for SW self-destruct messages
// When SW detects stale cache, it notifies us to reload
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'SW_STALE_CACHE_CLEARED') {
      console.log('[SW-CLIENT] 🔄 Stale cache cleared by service worker');
      console.log('[SW-CLIENT] Old version:', event.data.oldVersion);
      console.log('[SW-CLIENT] New version:', event.data.newVersion);

      addDebug('SW cleared stale cache, reloading...');

      // Small delay to ensure SW unregisters
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  });
}

// Mobile fix: Clean up old service workers and DON'T register new ones on mobile
// Service workers might be interfering with auth on mobile networks
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  addDebug('Checking for old service workers...');
  import('./utils/gentle-sw-cleanup').then(async ({ gentleServiceWorkerCleanup }) => {
    const needsReload = await gentleServiceWorkerCleanup();

    if (needsReload) {
      addDebug('Old service workers cleaned, reloading...');
      window.location.reload();
      return; // Stop here, page will reload
    }

    if (isMobile) {
      // Register public/sw.js (tile-only) on mobile — safe because it ignores all auth/Supabase URLs
      // Do NOT register virtual:pwa-register (the Workbox SW) — that's what caused auth blocking
      addDebug('Mobile detected - registering tile-only SW...');
      try {
        await navigator.serviceWorker.register('/sw.js', { scope: '/' });
        addDebug('Tile SW registered for mobile');
      } catch (err) {
        addDebug('Tile SW failed (non-fatal): ' + (err as Error).message);
      }
      return; // Skip the full Vite PWA registration
    }

    // DESKTOP: Register service worker normally
    addDebug('Starting service worker (desktop only)...');
    import('virtual:pwa-register').then(({ registerSW }) => {
    const updateSW = registerSW({
      onNeedRefresh() {
        if (confirm('New content available. Refresh?')) {
          updateSW(true)
        }
      },
      onOfflineReady() {
        addDebug('Service worker: ready offline');
        console.log('App ready to work offline')
      },
      onRegisterError(error) {
        addDebug('Service worker failed: ' + error.message);
        console.error('Service worker registration failed:', error);
        // Don't block app load - service worker is optional
      }
    })
    addDebug('Service worker registered');
  }).catch((error) => {
    addDebug('PWA setup error: ' + error.message);
    console.error('PWA setup failed:', error);
    // Continue loading app - PWA features are optional
  });
  }).catch((error) => {
    addDebug('SW cleanup error: ' + error.message);
    console.error('Service worker cleanup failed:', error);
    // Continue loading app even if cleanup fails
  });
} else {
  addDebug('Service worker skipped (dev mode or not supported)');
}

// Development debugging helpers
if (import.meta.env.DEV) {
  // Make cache testing available globally
  import('./services/api/cache-trigger.service').then(({ CacheTriggerService }) => {
    const cacheService = new CacheTriggerService();
    (window as any).testCacheGeneration = cacheService.testCacheGeneration.bind(cacheService);
    (window as any).backfillAllMissingRounds = cacheService.backfillAllMissingRounds.bind(cacheService);
    (window as any).fixZeroTotalScores = cacheService.fixZeroTotalScores.bind(cacheService);
    (window as any).checkCacheStatus = cacheService.checkCacheStatus.bind(cacheService);
    (window as any).fixMissingCompletedAt = cacheService.fixMissingCompletedAt.bind(cacheService);
    console.log('🧪 Debug helpers loaded: testCacheGeneration(), backfillAllMissingRounds(), fixZeroTotalScores(), checkCacheStatus(), and fixMissingCompletedAt() are now available');
  });
}

addDebug('Creating React root...');
const container = document.getElementById("root");
if (!container) {
  addDebug('ERROR: root element not found!');
  throw new Error('Root element not found');
}

addDebug('Rendering App component...');
try {
  const root = createRoot(container!);
  root.render(<App />);
  addDebug('App rendered successfully');
} catch (e) {
  addDebug('RENDER ERROR: ' + (e as Error).message);
  throw e;
}
// Latest deployment Mon Sep 15 17:17:59 CEST 2025
